import { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { MailMetadata } from '../../../interface/mailMetadata';
import TransactionCard from '../transactionCard';
import TaskCard from '../../task/taskCard';
import { ITransaction } from '../../../interface/transaction';
import { useUser } from '../../../context/UserContext';
import { ROUTES, STATUS_CODE, UserType } from '../../../constants/constants';
import { useFetchTransactionById } from '../../../hooks/useFetchTransactionById';
import { Paragraph, Spin, Text } from '../../Ant';
import { useTranslation } from 'react-i18next';
import { useFetchTaskDetail } from '../../../hooks/useFetchTaskDetail';
import { ITask } from '../../../interface/tasks';
import { useTransaction } from '../../../context/TransactionContext';
import RenderTransactionRestrictions from './renderTransactionRestrictions';
import { IMatter } from '../../../interface/matter';
import { Flex } from 'antd';
import CriticalCardMail from './criticalCardMail';
import { taskFields, taskRelations } from '../../../constants/tasks';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useCopyTransaction } from '../../../hooks/useCopyTransaction';
import { InvoiceableType } from '../../../constants/transaction';
import { getLocalDateAndTime } from '../../../constants/utilityFunctions';
import { useEmailCategoryTagger } from '../../../hooks/useEmailCategoryTagger';
import LinkedItemNotFoundModal from './linkedItemNotFoundModal';
import NoData from '../noData';
import { GeneralEmpty } from '../../icons';

const isEntityNotFound = (error: unknown) =>
  (error as AxiosError)?.response?.status === STATUS_CODE.ENTITY_NOT_FOUND;

const ShowMetadataDetails = ({
  metadata,
  updateSelectedMatter,
  onItemUnlinked,
}: {
  metadata: MailMetadata;
  updateSelectedMatter: (matter: IMatter) => void;
  onItemUnlinked?: () => void;
}) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const [selectedTask, setSelectedTask] = useState<ITask>();
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const [isTransactionEdit, setIsTransactionEdit] = useState<boolean>();
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const { updateTransaction } = useTransaction();
  const { user } = useUser();
  const { untagEmailCategory } = useEmailCategoryTagger();

  const {
    data: transactionDetails,
    error: transactionError,
    isLoading: isTransactionLoading,
  } = useFetchTransactionById({
    id: metadata.transactionId ?? '',
    skipGlobalErrorHandler: true,
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'transactionUser:fullName',
      'transactionType:translations,standardPrice,ignoreDiscount',
      'skill:name',
      'unit:translations',
    ],
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'workedHours',
      'unitId',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'hasImportantNotes',
      'hasNotes',
    ],
  });
  const {
    data: task,
    error: taskError,
    isLoading: isTaskLoading,
  } = useFetchTaskDetail({
    id: metadata?.taskId,
    skipGlobalErrorHandler: true,
    relations: [
      'matter.parties',
      'matter.parties.company',
      'matter.parties.privatePerson',
      'matter:subject,matterNumber,currency,invoiceableType',
      'responsibleUser:fullName',
      'status',
      'transaction',
    ],
    fields: taskFields,
  });
  const {
    data: criticalTask,
    error: criticalTaskError,
    isLoading: isCriticalTaskLoading,
  } = useFetchTaskDetail({
    id: metadata?.criticalTaskId,
    skipGlobalErrorHandler: true,
    relations: taskRelations,
    fields: taskFields,
  });
  const isLoadingLinkedItem = isTransactionLoading || isTaskLoading || isCriticalTaskLoading;
  const hasNoLinkedItemData = !transactionDetails && !task && !criticalTask;
  const missingLinkedItem = useMemo(() => {
    const candidates = [
      {
        id: metadata.transactionId,
        field: 'transactionId' as const,
        title: t('transaction:doesNotExist'),
        categoryName: `[CSI - ${t('transaction:transaction')}]`,
        error: transactionError,
      },
      {
        id: metadata.taskId,
        field: 'taskId' as const,
        title: t('task:doesNotExist'),
        categoryName: `[CSI - ${t('task:task')}]`,
        error: taskError,
      },
      {
        id: metadata.criticalTaskId,
        field: 'criticalTaskId' as const,
        title: t('task:criticalTaskDoesNotExist'),
        categoryName: `[CSI - ${t('task:criticalTask.criticalTask')}]`,
        error: criticalTaskError,
      },
    ];
    return (
      candidates.find((candidate) => candidate.id && isEntityNotFound(candidate.error)) ?? null
    );
  }, [metadata, transactionError, taskError, criticalTaskError, t]);

  const handleConfirmUnlinkMissingItem = async () => {
    if (!missingLinkedItem) return;
    await untagEmailCategory(missingLinkedItem.categoryName, { [missingLinkedItem.field]: '' });
    onItemUnlinked?.();
  };

  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true ||
    user?.userType === UserType.RESTRICTED ||
    transactionDetails?.matter?.invoiceableType === InvoiceableType.INTERNAL;
  useEffect(() => {
    const matterFromTask = task?.matter ?? criticalTask?.matter;
    const matterFromTransaction = transactionDetails?.matter;

    const selectedMatter = matterFromTask ?? matterFromTransaction;

    if (selectedMatter?.id) {
      const enrichedMatter = { ...selectedMatter };

      // Fill missing keys from task or transactionDetails
      if (!enrichedMatter.principalCompany && transactionDetails?.principalCompany) {
        enrichedMatter.principalCompany = transactionDetails?.principalCompany;
      }

      if (!enrichedMatter.principalPrivatePerson && transactionDetails?.principalPrivatePerson) {
        enrichedMatter.principalPrivatePerson = transactionDetails?.principalPrivatePerson;
      }

      updateSelectedMatter(enrichedMatter);
    }
  }, [task, transactionDetails, criticalTask]);

  const handleTaskToTransaction = (transaction?: ITransaction) => {
    if (transaction && !isTransactionEdit && !copyClicked) {
      // Task to transaction flow will be implemented
      updateTransaction({
        matterId: selectedTask?.matterId,
        matter: selectedTask?.matter,
        subject: selectedTask?.subject,
        currentSubject: selectedTask?.subject,
        isManualUnitPrice: false,
        shouldLoadServerPrice: true,
        principalCompany: selectedTask?.matter?.principalCompany,
        principalPrivatePerson: selectedTask?.matter?.principalPrivatePerson,
        overrideWarnings: true,
        activityId: selectedTask?.id,
      } as ITransaction);
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else if (transaction && !copyClicked) {
      // Transaction edit flow will be implemented
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      // Transaction copy flow will be implemented
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      setIsTransactionEdit(false);
      setCopyClicked(false);
      setSelectedTask(undefined);
      setSelectedTransaction(undefined);
    }
  };
  const handleCopyTransaction = (transaction: ITransaction) => {
    copyTransaction(transaction);
  };
  const cardHeader = (title: string, date: string, testId: string) => {
    return (
      <div className="d-flex justify-between items-center w-full">
        <Paragraph
          $fontsize="12px"
          $lineheight="16px"
          $color="description"
          className="whitespace-nowrap"
          $width="100%"
          $weight={500}
          data-testid={testId}
        >
          {title}
        </Paragraph>
        <Text
          $fontsize="12px"
          $lineheight="18px"
          $color="subtitle"
          $weight={400}
          $width="100%"
          className="ta-right"
          data-testid={`${testId}-date`}
        >
          {date}
        </Text>
      </div>
    );
  };
  return (
    <Flex vertical gap={10} className="overflow-y-auto scroll-stable-gutter pb-gutter h-full">
      {isLoadingLinkedItem && (
        <div className="h-full d-flex justify-center items-center">
          <Spin size="large" />
        </div>
      )}
      {transactionDetails && (
        <Flex vertical gap={10}>
          {cardHeader(
            t('transaction:createdTransaction'),
            getLocalDateAndTime(new Date(transactionDetails.transactionDate), true),
            'created-transaction',
          )}
          <TransactionCard
            data-testid="transaction-card"
            key={transactionDetails.id}
            transaction={transactionDetails}
            hidden={hidePricingInfo}
            onEdit={(transaction) => {
              setIsTransactionEdit(true);
              setSelectedTransaction(transaction);
            }}
            onCopy={(transaction) => {
              handleCopyTransaction(transaction);
            }}
            onNewToSameMatter={startTransactionOnSameMatter}
          />
        </Flex>
      )}
      {task && (
        <Flex vertical gap={10}>
          {cardHeader(
            t('task:createdTask'),
            getLocalDateAndTime(new Date(task.startTime), true),
            'created-task',
          )}
          <TaskCard
            key={task?.id}
            task={{ activity: task, currentOfTotalDays: '' }}
            handleRestrictions={(task: ITask, transaction: ITransaction) => {
              setSelectedTask(task);
              if (task?.matterId) {
                setSelectedTransaction(transaction);
              } else {
                handleTaskToTransaction({
                  matter: task?.matter,
                  matterId: task?.matterId,
                } as ITransaction);
              }
            }}
          />
        </Flex>
      )}
      {criticalTask && (
        <Flex vertical gap={10}>
          {cardHeader(
            t('task:createdCriticalTask'),
            getLocalDateAndTime(new Date(criticalTask.startTime), true),
            'created-critical-task',
          )}
          <CriticalCardMail criticalTask={criticalTask} />
        </Flex>
      )}
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleTaskToTransaction}
      />
      {!isLoadingLinkedItem && !missingLinkedItem && hasNoLinkedItemData && (
        <div className="h-full d-flex justify-center items-center">
          <NoData header={t('common:noData')} icon={<GeneralEmpty />} />
        </div>
      )}
      {missingLinkedItem && (
        <LinkedItemNotFoundModal
          title={missingLinkedItem.title}
          onConfirm={handleConfirmUnlinkMissingItem}
        />
      )}
    </Flex>
  );
};

export default ShowMetadataDetails;
